import { useEffect, useRef, useState } from "react";
import { useAuth } from "hooks/useAuth";

export interface Customer {
  customerId: number;
  name?: string;
  cpf?: string;
  phoneNumber?: string;
  email?: string;
  lastTimeLogged?: string;
  isConfi: boolean;
}

export interface PersonSelectPayload {
  customer: Customer;
  unmasked: {
    cpf: string;
    email: string;
    phoneNumber: string;
  };
}

export type DateFilter = "none" | "24h" | "week" | "specific";

const ROWS_PER_PAGE = 20;

const computeDateRange = (
  filter: DateFilter,
  specificDate: Date | null
): { registerMinDate?: string; registerMaxDate?: string } => {
  const now = new Date();

  switch (filter) {
    case "24h": {
      const from = new Date(now.getTime() - 24 * 60 * 60 * 1000);
      return { registerMinDate: from.toISOString(), registerMaxDate: now.toISOString() };
    }
    case "week": {
      const monday = new Date(now);
      const day = monday.getDay();
      const diff = day === 0 ? -6 : 1 - day;
      monday.setDate(monday.getDate() + diff);
      monday.setHours(0, 0, 0, 0);
      return { registerMinDate: monday.toISOString(), registerMaxDate: now.toISOString() };
    }
    case "specific": {
      if (!specificDate) return {};
      const from = new Date(specificDate);
      from.setHours(0, 0, 0, 0);
      const to = new Date(specificDate);
      to.setHours(23, 59, 59, 999);
      return { registerMinDate: from.toISOString(), registerMaxDate: to.toISOString() };
    }
    default:
      return {};
  }
};

const buildSearchUrl = (
  keyword: string,
  page: number,
  filter: DateFilter,
  specificDate: Date | null
): string => {
  const { registerMinDate, registerMaxDate } = computeDateRange(filter, specificDate);
  const parts = [
    `customers?KeyWord=${encodeURIComponent(keyword)}`,
    `PageNumber=${page}`,
    `RowsPerPage=${ROWS_PER_PAGE}`,
    registerMinDate ? `RegisterMinDate=${encodeURIComponent(registerMinDate)}` : "",
    registerMaxDate ? `RegisterMaxDate=${encodeURIComponent(registerMaxDate)}` : "",
  ].filter(Boolean);
  return parts.join("&");
};

export const usePersonSearch = () => {
  const { connectApi, cancelRequest } = useAuth();

  const [searchText, setSearchText] = useState("");
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isUnmasking, setIsUnmasking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [dateFilter, setDateFilter] = useState<DateFilter>("none");
  const [specificDate, setSpecificDate] = useState<Date | null>(null);

  const pageRef = useRef(1);
  const lastKeywordRef = useRef("");
  const isFirstRenderRef = useRef(true);

  const runSearch = async (
    keyword: string,
    page: number,
    append: boolean,
    filter: DateFilter,
    specDate: Date | null
  ) => {
    pageRef.current = page;
    setIsLoading(true);
    setError(null);
    if (!append) cancelRequest();

    try {
      const response = await connectApi({
        type: "get",
        url: buildSearchUrl(keyword, page, filter, specDate),
        errorMessage: "Falha ao buscar clientes",
      });

      const data = response.data;
      const results: Customer[] = data.customers ?? [];
      const total = parseInt(data.properties?.[0]?.value ?? "0");

      if (append) {
        setCustomers((prev) => {
          const merged = [...prev, ...results];
          setHasMore(merged.length < total);
          return merged;
        });
      } else {
        setCustomers(results);
        setHasMore(results.length < total);
      }
      setHasSearched(true);
    } catch (err: any) {
      if (err?.errorType !== "cancellation") {
        setError("Erro ao buscar clientes. Tente novamente.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = async () => {
    const kw = searchText.trim();
    lastKeywordRef.current = kw;
    await runSearch(kw, 1, false, dateFilter, specificDate);
  };

  const handleLoadMore = async () => {
    if (isLoading || !hasMore) return;
    const nextPage = pageRef.current + 1;
    await runSearch(lastKeywordRef.current, nextPage, true, dateFilter, specificDate);
  };

  // Auto-refresh when filter changes and results are already showing.
  // Skips the initial mount and skips "specific" until a date is selected.
  useEffect(() => {
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false;
      return;
    }
    if (!hasSearched) return;
    if (dateFilter === "specific" && !specificDate) return;

    runSearch(lastKeywordRef.current, 1, false, dateFilter, specificDate);
  }, [dateFilter, specificDate]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    return () => cancelRequest();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchUnmasked = async (
    customer: Customer,
    onSelect: (payload: PersonSelectPayload) => void
  ) => {
    setIsUnmasking(true);

    try {
      const [cpfRes, emailRes, phoneRes] = await Promise.all([
        connectApi({
          type: "get",
          url: `customers/${customer.customerId}/unmask?field=cpf`,
          errorMessage: "Falha ao obter CPF",
        }),
        connectApi({
          type: "get",
          url: `customers/${customer.customerId}/unmask?field=email`,
          errorMessage: "Falha ao obter email",
        }),
        connectApi({
          type: "get",
          url: `customers/${customer.customerId}/unmask?field=phoneNumber`,
          errorMessage: "Falha ao obter telefone",
        }),
      ]);

      onSelect({
        customer,
        unmasked: {
          cpf: cpfRes.data?.cpf ?? "",
          email: emailRes.data?.email ?? "",
          phoneNumber: phoneRes.data?.phoneNumber ?? "",
        },
      });
    } catch {
      // connectApi already shows a toast on error; modal stays open
    } finally {
      setIsUnmasking(false);
    }
  };

  return {
    searchText,
    setSearchText,
    customers,
    isLoading,
    isUnmasking,
    error,
    hasMore,
    hasSearched,
    dateFilter,
    setDateFilter,
    specificDate,
    setSpecificDate,
    handleSearch,
    handleLoadMore,
    fetchUnmasked,
  };
};
